import { gold } from "@ant-design/colors"
import { faCircleExclamation } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Divider, Form, Modal, Select, Space, Table, Tag, Typography, notification } from "antd"
import dayjs from "dayjs"
import { useContext, useEffect, useState } from "react"
import { AuthenticationContext } from "../../AuthenticationContext"
import { CatalogItem } from "../../domain/catalogItem"
import { hasValue } from "../../helpers/nullable"
import { useBudget } from "../Budgets/budgetQueries"
import PercentInput from "../Budgets/PercentInput"
import { useLaborItemsQuery } from "../ItemCatalog/apiQueries"
import LabeledSwitch from "../common/LabeledSwitch"
import VSpace from "../common/VSpace"
import BudgetCalculatorContext from "./BudgetCalculatorContext"
import { formatDollarsAndCents } from "./budgetCalculatorModel"
import {
  applyOutcomeNotification,
  applyYearOptions,
  averageWageInCents,
  BudgetSettings,
  budgetSettingsFrom,
  defaultApplyYear,
  defaultLaborItemIds,
  laborItemRoles,
  laborPricingAvailability,
  laborPricingUnavailableMessage,
  taxRateInputValue,
  toApplyWorksheetRequest,
  withTaxRateInput,
  zeroWageWarning
} from "./applyWorksheetModel"
import { useApplyWorksheetMutation } from "./budgetQueries"

const { Text } = Typography

export type Props = {
  open: boolean
  onCancel: () => void
}

const Warning = ({ message }: { message: string }) => <Text style={{ color: gold[7] }}>
  <FontAwesomeIcon icon={faCircleExclamation} style={{ marginRight: 8 }} />
  {message}
</Text>

const ApplyWorksheetModal = ({ open, onCancel }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const { worksheet, summary, statistics, budgets, saveWorksheetNow } = useContext(BudgetCalculatorContext)
  const [year, setYear] = useState<number | null>(null)
  const [selectedItemIds, setSelectedItemIds] = useState<number[] | null>(null)
  const [settings, setSettings] = useState<BudgetSettings | null>(null)
  const [updateUnsoldJobs, setUpdateUnsoldJobs] = useState(false)
  const [isApplying, setIsApplying] = useState(false)

  const years = applyYearOptions(budgets)
  const tenant = currentUser?.tenant
  const laborItemsQuery = useLaborItemsQuery(open)
  const targetBudgetsQuery = useBudget(year)
  const applyMutation = useApplyWorksheetMutation()

  useEffect(() => {
    if (open) {
      setYear(defaultApplyYear(years, dayjs().year()))
      setSelectedItemIds(null)
      setSettings(null)
      setUpdateUnsoldJobs(false)
      setIsApplying(false)
    }
  }, [open])

  useEffect(() => {
    if (!hasValue(year)) {
      setYear(defaultApplyYear(years, dayjs().year()))
    }
  }, [years.length])

  useEffect(() => {
    setSettings(null)
  }, [year])

  useEffect(() => {
    if (open && !hasValue(settings)) {
      setSettings(budgetSettingsFrom(targetBudgetsQuery.data))
    }
  }, [open, settings, targetBudgetsQuery.data])

  const laborItems = laborItemsQuery.data ?? []

  useEffect(() => {
    if (open && !hasValue(selectedItemIds) && hasValue(laborItemsQuery.data)) {
      setSelectedItemIds(defaultLaborItemIds(tenant, laborItemsQuery.data))
    }
  }, [open, tenant, laborItemsQuery.data, selectedItemIds])

  const availability = laborPricingAvailability({
    isLoading: laborItemsQuery.isLoading,
    isError: laborItemsQuery.isError,
    items: laborItems
  })
  const averageWage = averageWageInCents(summary)
  const unavailableMessage = laborPricingUnavailableMessage(availability)
  const wageWarning = zeroWageWarning(availability, averageWage)
  const selectedIds = selectedItemIds ?? []

  const handleApply = async () => {
    const targetBudgets = targetBudgetsQuery.data
    if (!hasValue(year) || !hasValue(targetBudgets) || !hasValue(settings) || isApplying) {
      return
    }

    setIsApplying(true)
    await saveWorksheetNow()
    applyMutation.mutate(toApplyWorksheetRequest({
      worksheet,
      statistics,
      year,
      targetBudgets,
      settings,
      laborItems,
      selectedItemIds: selectedIds,
      averageWageInCents: averageWage,
      updateUnsoldJobs
    }), {
      onSuccess: result => {
        const outcome = applyOutcomeNotification(year, result)
        notification[outcome.type]({ message: outcome.message })
        onCancel()
      },
      onSettled: () => setIsApplying(false)
    })
  }

  return <Modal
    open={open}
    title="Apply to Budget"
    okText="Apply"
    onOk={handleApply}
    onCancel={onCancel}
    confirmLoading={isApplying}
    okButtonProps={{ disabled: !hasValue(settings) || availability === 'loading' }}
    width={680}
  >
    <VSpace size="middle">
      <Form layout="vertical">
        <Space size="large" align="start">
          <Form.Item label="Budget year" style={{ marginBottom: 0 }}>
            <Select
              value={year}
              onChange={setYear}
              options={years.map(option => ({ label: option.toString(), value: option }))}
              style={{ width: 160 }}
            />
          </Form.Item>
          <Form.Item label="Tax rate" style={{ marginBottom: 0 }}>
            <PercentInput
              value={taxRateInputValue(settings)}
              disabled={!hasValue(settings)}
              onChange={fraction => setSettings(current => hasValue(current) ? withTaxRateInput(current, fraction) : current)}
              style={{ width: 160 }}
            />
          </Form.Item>
        </Space>
      </Form>

      <LabeledSwitch
        value={settings?.isDefault ?? false}
        disabled={!hasValue(settings)}
        onChange={isDefault => setSettings(current => hasValue(current) ? { ...current, isDefault } : current)}
        label="This is the default budget"
      />

      {targetBudgetsQuery.isError && <Warning message="Your budget for this year could not be loaded. Please try again in a few minutes." />}

      <Divider style={{ margin: 0 }} />

      <VSpace size={4}>
        <LabeledSwitch
          value={updateUnsoldJobs}
          onChange={setUpdateUnsoldJobs}
          label="Update item pricing on unsold jobs"
        />
        <Text type="secondary">
          Reprices every unsold job that uses this budget. Leave this off to use the new numbers only on jobs created in the future.
        </Text>
      </VSpace>
      <Divider style={{ margin: 0 }} />

      <VSpace size={8}>
        <Text strong>Labor rates</Text>
        <Text type="secondary">
          Sets the cost of the checked items to the average wage of {formatDollarsAndCents(averageWage / 100)} per hour
          and clears any custom price. Your default labor items start checked.
        </Text>
        <Table<CatalogItem>
          rowKey="id"
          size="small"
          dataSource={laborItems}
          loading={laborItemsQuery.isLoading}
          pagination={false}
          scroll={{ y: 220 }}
          rowSelection={{
            selectedRowKeys: selectedIds,
            onChange: keys => setSelectedItemIds(keys as number[])
          }}
          columns={[
            {
              dataIndex: 'name',
              title: 'Item',
              render: (name, item) => <Space size={4} wrap={false}>
                <span style={{ whiteSpace: 'nowrap' }}>{name}</span>
                {laborItemRoles(tenant, item).map(role => <Tag key={role} color="blue">{role}</Tag>)}
              </Space>
            },
            { dataIndex: 'sku', title: 'SKU', width: 140 },
            {
              dataIndex: 'cost',
              title: 'Current Cost',
              align: 'right',
              width: 130,
              render: cost => formatDollarsAndCents(cost / 100)
            }
          ]}
        />
        {hasValue(unavailableMessage) && <Warning message={unavailableMessage} />}
        {hasValue(wageWarning) && <Warning message={wageWarning} />}
      </VSpace>
    </VSpace>
  </Modal>
}

export default ApplyWorksheetModal
